import React from 'react';
import { Alert, Flex, Button, Spin, theme } from 'antd';

import './OnboardingSurvey.css';
import QUESTIONS, { type Answers, type Question, QuestionCodes, SKIPPED_ANSWER } from './questions';
import SurveyHeading from './SurveyHeading';
import SurveyOptions from './SurveyOptions';
import { useEvent } from '../events/hooks/useEvent';
import { useLazyGetAnswersQuery, useUpdateAnswersMutation } from './OnboardingSurveyApi';
import findFirstUnansweredIndex from './findFirstUnansweredIndex';

const FULL_SURVEY = QUESTIONS.filter((question) => !question.hidden);

// Invited (non-account-owner) users only answer the work type question. The list
// is explicit by question code, so hiding or reordering questions elsewhere can
// never silently change what the short survey asks (PRE-3134).
//
// Server-side counterpart: `isShortSurveyComplete` / `isFullSurveyComplete` in
// packages/prerender-shared/src/marketing-utils.ts decide whether a stored record
// counts as complete. Both sides must ask for the same answers — a survey that
// asks less than the completeness check requires puts users in a redirect loop.
const SHORT_SURVEY_CODES: Question['code'][] = [QuestionCodes.Department];
const SHORT_SURVEY = QUESTIONS.filter((question) => SHORT_SURVEY_CODES.includes(question.code));

// The prefill of previous answers is best-effort: a new account has none, and the
// request can hang pending (blocked/offline) — which used to leave the loader
// spinning forever. Never block the survey on it longer than this (PRE-3299).
const PREFILL_TIMEOUT_MS = 4000;

// Saving an answer used to be fire-and-forget: the survey moved on whether or not
// the write landed, so a lost answer was invisible until the next login bounced the
// user back to question 1 with nothing to click but Continue. The survey now waits
// for the write, reports a failure, and — once retrying has plainly not helped —
// offers a way out (PRE-3350).
const FAILED_SAVES_BEFORE_ESCAPE = 2;

type Failure = {
  event: string;
  message: string;
  description: string;
};

const ANSWER_SAVE_FAILURE: Failure = {
  event: 'Onboarding Survey: Answer Save Failed',
  message: 'We could not save your answer',
  description: 'Please check your connection and try again.',
};

// The answer is stored, but the server does not count the survey as finished, so
// letting the user through would only send them back here.
const COMPLETION_FAILURE: Failure = {
  event: 'Onboarding Survey: Completion Failed',
  message: 'We could not confirm your answers',
  description: 'Your answers were saved. Please try again.',
};

// What a completion handler reports back. A handler that returns nothing is taken at
// its word; `confirmed: false` says the answers are stored but the server still does
// not count the survey as finished, which is an outcome rather than an error — a
// rejection means the handler could not find out at all.
export type SurveyCompletion = { confirmed: boolean };

type OnboardingSurveyProps = {
  isShortSurvey?: boolean;
  getContinueLabel?: (answers: Answers) => string;
  onCompleteSurvey?: (answers: Answers) => void | Promise<void | SurveyCompletion>;
  // Called when the user gives up after repeated failed saves. The caller decides
  // where they land; the survey only stops asking.
  onAbandonSurvey?: () => void;
};
function OnboardingSurvey({
  isShortSurvey,
  onCompleteSurvey,
  onAbandonSurvey,
  getContinueLabel,
}: OnboardingSurveyProps) {
  const survey = isShortSurvey ? SHORT_SURVEY : FULL_SURVEY;

  const [getAnswers, userAnswers] = useLazyGetAnswersQuery();
  const [updateAnswers] = useUpdateAnswersMutation();

  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
  const [value, setValue] = React.useState<string>();
  const [userInput, setUserInput] = React.useState<string>();
  const [prefillPending, setPrefillPending] = React.useState(true);
  const [savePending, setSavePending] = React.useState(false);
  const [failure, setFailure] = React.useState<Failure>();
  // Counted across questions, so moving between them does not take the way out away
  // again. A successful step does reset it: the user is making progress.
  const [failedSaveCount, setFailedSaveCount] = React.useState(0);

  const { track } = useEvent();
  const { token } = theme.useToken();

  const currentQuestion = survey[currentQuestionIndex];
  const currentAnswerData = currentQuestion.options.find((answer) => answer.value === value);
  const isLastQuestion = currentQuestionIndex === survey.length - 1;

  React.useEffect(() => {
    let settled = false;
    const finishPrefill = () => {
      settled = true;
      setPrefillPending(false);
    };

    getAnswers()
      .unwrap()
      .then((response) => {
        const lastQuestionIndex = findFirstUnansweredIndex(survey, response);
        setCurrentQuestionIndex(lastQuestionIndex);
      })
      // A new account has no saved answers, and the request can fail or hang
      // (blocked/offline); don't hold the survey hostage — fall through to Q0.
      .catch(() => undefined)
      .finally(finishPrefill);

    // Safety valve: never leave the survey blocked on a request that never settles.
    const timeout = setTimeout(() => {
      if (!settled) setPrefillPending(false);
    }, PREFILL_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, []);

  React.useEffect(() => {
    if (userAnswers.data) {
      const existingAnswer = userAnswers.data?.[currentQuestion.code];
      const existingAnswerData = currentQuestion.options.find((answer) => answer.value === existingAnswer);

      if (!existingAnswer || existingAnswer === SKIPPED_ANSWER) {
        setValue(undefined);
        setUserInput(undefined);
        return;
      }

      // previous answer is not empty
      if (existingAnswerData) {
        setValue(existingAnswerData.value);
        return;
      }

      // no exact match found, might be free input if there is an input option
      const inputOption = currentQuestion.options.find((answer) => answer.input);
      if (inputOption) {
        setValue(inputOption.value);
        setUserInput(existingAnswer);
      }
    }
  }, [currentQuestionIndex]);

  const canContinue = React.useMemo(() => {
    if (currentQuestion.optional) return true;

    if (!currentAnswerData) return false;

    if (currentAnswerData.input && !userInput) return false;

    return true;
  }, [currentQuestion, currentAnswerData, userInput]);

  const changeQuestion = (goBack = false) => {
    setValue(undefined);
    setUserInput(undefined);
    // The error belongs to the answer being left behind.
    setFailure(undefined);
    setCurrentQuestionIndex((prev) => (goBack ? prev - 1 : prev + 1));
  };

  // Writes the current answer, and on the last question asks the caller to confirm
  // the survey against the server. Returns how it failed rather than setting state,
  // so every state change for a step stays in `onContinue`.
  const submitCurrentQuestion = async (): Promise<Failure | undefined> => {
    const currentAnswer = currentAnswerData?.input ? userInput : value;
    const previousAnswer = userAnswers.data?.[currentQuestion.code];
    const isSkipped = !currentAnswer || (currentAnswer === SKIPPED_ANSWER && currentQuestion.optional);
    const answerToSave = isSkipped ? SKIPPED_ANSWER : currentAnswer;
    // An unchanged answer is already stored, so there is nothing to write.
    const needsSave = isSkipped || answerToSave !== previousAnswer;

    if (needsSave) {
      try {
        await updateAnswers({ [currentQuestion.code]: answerToSave }).unwrap();
      } catch {
        return ANSWER_SAVE_FAILURE;
      }
      track(`Onboarding Survey: ${currentQuestion.code} Survey Submitted`, { [currentQuestion.code]: answerToSave });
    }

    if (!isLastQuestion) return undefined;

    // Report only the answers to the questions this survey actually presents: a
    // stored record can still carry answers to hidden questions (PRE-3134).
    const answerFor = (code: Question['code']) =>
      code === currentQuestion.code ? answerToSave : userAnswers.data?.[code];
    const data: Answers = Object.fromEntries(
      survey.map((question) => [question.code, answerFor(question.code)]).filter(([, answer]) => answer)
    );

    let completion;
    try {
      completion = await onCompleteSurvey?.(data);
    } catch {
      return COMPLETION_FAILURE;
    }
    if (completion && !completion.confirmed) return COMPLETION_FAILURE;

    track('Onboarding Survey: Survey Completed', data);
    return undefined;
  };

  // Neither failure leaves any other record — production rest-api does not log
  // requests and the ingress access logs are gone within minutes — so each one
  // reports its own event, and its own message, to the user (PRE-3350).
  const reportFailure = (stepFailure: Failure) => {
    track(stepFailure.event, { question: currentQuestion.code });
    setFailure(stepFailure);
    setFailedSaveCount((count) => count + 1);
  };

  const onContinue = async () => {
    setSavePending(true);
    const stepFailure = await submitCurrentQuestion();
    setSavePending(false);

    if (stepFailure) {
      reportFailure(stepFailure);
      return;
    }

    if (!isLastQuestion) changeQuestion();
    setFailure(undefined);
    setFailedSaveCount(0);
  };

  const onBack = () => {
    changeQuestion(true);
  };

  return (
    <div>
      <Spin spinning={prefillPending} tip="Loading you previous answers..." size="large">
        <div style={{ marginTop: token.marginXL, marginBottom: token.marginXL }}>
          <SurveyHeading
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={survey.length}
            question={currentQuestion.title}
            questionHint={currentQuestion.inlineSubtitle}
          />

          <SurveyOptions
            options={currentQuestion.options}
            optionSelected={value}
            onOptionSelected={setValue}
            onInputChange={setUserInput}
            userInput={userInput}
          />
        </div>
        {failure && (
          <Alert
            type="error"
            showIcon
            message={failure.message}
            description={failure.description}
            style={{ marginBottom: token.marginMD }}
          />
        )}

        <Flex justify="center" gap="middle">
          {currentQuestionIndex > 0 && (
            <Button size="large" disabled={savePending} onClick={onBack}>
              Back
            </Button>
          )}
          <Button
            size="large"
            disabled={!canContinue || savePending}
            loading={savePending}
            type="primary"
            onClick={onContinue}
          >
            {isLastQuestion && getContinueLabel ? getContinueLabel(userAnswers.data || {}) : 'Continue'}
          </Button>
        </Flex>

        {onAbandonSurvey && failedSaveCount >= FAILED_SAVES_BEFORE_ESCAPE && (
          <Flex justify="center" style={{ marginTop: token.marginMD }}>
            <Button type="link" onClick={onAbandonSurvey}>
              Continue without answering
            </Button>
          </Flex>
        )}
      </Spin>
    </div>
  );
}

export default OnboardingSurvey;
